// TableFloatingMenuComponent.jsx
// React component for the table floating menu.
// Renders a compact button-group toolbar above selected table cells.
//
// SOLID notes
// S – Only responsible for rendering the toolbar UI.
//     Command enablement/dispatch is injected via props (Dependency Inversion).
// O – New groups can be added to tableFloatingMenuConfigs.js without touching this file.

import React, {useCallback} from "react";
import {Button, ButtonGroup, Divider, Paper, Tooltip} from "@mui/material";
import {CELL_CURSOR_CONFIGS, CELL_RANGE_CONFIGS} from "./tableFloatingMenuConfigs";

// ─── Module-level memos ───────────────────────────────────────────────────────
// Group order defines the left-to-right visual separation of button clusters.
const GROUP_ORDER = ["merge", "header", "row", "col", "delete"];

/**
 * Split a flat config array into ordered, non-empty groups.
 * Computed once at module load — configs are static constants.
 *
 * @param {Array} configs
 * @returns {Array<Array>}
 */
function buildGroups(configs) {
    const map = Object.create(null);
    for (const c of configs) {
        if (!map[c.group]) map[c.group] = [];
        map[c.group].push(c);
    }
    return GROUP_ORDER.map(g => map[g]).filter(Boolean);
}

// Pre-built group arrays — never re-allocated during render cycles.
const CURSOR_GROUPS = buildGroups(CELL_CURSOR_CONFIGS);
const RANGE_GROUPS  = buildGroups(CELL_RANGE_CONFIGS);

// ─── Sub-components ───────────────────────────────────────────────────────────

/**
 * A single button group cluster with optional leading divider.
 */
function ActionGroup({group, showDivider, onAction, isEnabled}) {
    const isDeleteGroup = group[0].group === "delete";

    return (
        <>
            {showDivider && (
                <Divider orientation="vertical" flexItem sx={{my: 0.5}}/>
            )}
            <ButtonGroup
                variant="text"
                size="small"
                color={isDeleteGroup ? "error" : "inherit"}
                sx={{flexWrap: "nowrap"}}
            >
                {group.map(config => {
                    const IconComponent = config.icon;
                    const enabled = isEnabled(config.type);
                    return (
                        <Tooltip key={config.type} title={config.title} arrow placement="top">
                            {/* span wrapper required by MUI Tooltip when child is disabled */}
                            <span>
                                <Button
                                    onClick={() => onAction(config.type)}
                                    startIcon={<IconComponent sx={{fontSize: "14px !important"}}/>}
                                    disabled={!enabled}
                                    sx={{
                                        flexShrink: 0,
                                        minWidth: "auto",
                                        whiteSpace: "nowrap",
                                        textTransform: "none",
                                        px: 1.25,
                                        py: 0.75,
                                        fontSize: "12px",
                                        fontWeight: 500,
                                        color: isDeleteGroup ? "error.main" : "text.primary",
                                        "&.Mui-disabled": {color: "text.disabled"},
                                    }}
                                >
                                    {config.label}
                                </Button>
                            </span>
                        </Tooltip>
                    );
                })}
            </ButtonGroup>
        </>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * TableFloatingMenuComponent
 *
 * @param {Object}   props
 * @param {"cursor"|"range"} props.mode      - Determines which action set to render.
 * @param {Function} props.onAction          - Called with actionType string.
 * @param {Function} props.isEnabled         - Called with actionType, returns boolean.
 */
export function TableFloatingMenuComponent({mode, onAction, isEnabled}) {
    const groups = mode === "range" ? RANGE_GROUPS : CURSOR_GROUPS;

    // Guard: don't render anything if mode is unexpectedly null/undefined.
    if (!mode || groups.length === 0) return null;

    return (
        <Paper
            elevation={3}
            sx={{
                display: "inline-flex",
                borderRadius: 1,
                overflow: "hidden",
                width: "max-content",
                maxWidth: "none",
                whiteSpace: "nowrap",
                alignItems: "center",
            }}
        >
            {groups.map((group, gi) => (
                <ActionGroup
                    key={group[0].group}
                    group={group}
                    showDivider={gi > 0}
                    onAction={onAction}
                    isEnabled={isEnabled}
                />
            ))}
        </Paper>
    );
}
